
'use client';

import Image from 'next/image';
import { bundleSizeOf } from '@/lib/product-bundles';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/ui/star-rating';
import { VatNotice } from '@/components/vat-notice';
import { cn } from '@/lib/utils';
import { skipImageOptimizer } from '@/lib/image-optimizer';
import { useTheme } from '@/context/theme-context';
import { PlaceholderMedia } from './ui/placeholder-media';
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Star as StarIcon, Bookmark, ShoppingCart, Download, Pencil, BarChart2 } from 'lucide-react';
import type { ProductImageObject } from '@/context/product-context';
import { useAuth } from '@/context/auth-context';
import { useOwnedProducts } from '@/context/owned-products-context';
import { useCart } from '@/context/cart-context';
import { useWishlist } from '@/context/wishlist-context';
import { useFirebase } from '@/firebase';
import { isSubscriptionTier, tierFrequency } from '@/lib/product-form-shared';
import { formatCycleSuffix } from '@/lib/subscriptions';
import { cartPriceAud } from '@/lib/free-pricing';
import { cheapestPriceTier, listingPriceAud, listingPriceDisplay, listingPriceIsFrom } from '@/lib/listing-price';
import { pwywMinimumLabelAud } from '@/lib/pwyw-seed';
import { doc, getDoc, type Firestore } from 'firebase/firestore';
import { UserAvatar } from '@/components/ui/user-avatar';
import { STOREFRONT_LINK_PROPS, storefrontHref } from '@/lib/storefront-url';
import { catalogAggregates } from '@/lib/live-catalog';

// Module-level caches dedupe identical fetches across simultaneously-mounted
// ProductCards. Without these, a 12-card marketplace render fires 12 seller
// reads, many of them duplicates (same seller across cards, same product
// re-rendering). The TTL keeps results fresh enough for a marketplace where
// avatars change relatively slowly. A card reads no reviews at all: see the
// rating below.
type SellerInfo = { avatarUrl: string; showPfp: boolean; username: string };
type ReviewInfo = { rating: number; count: number };
const SELLER_CACHE_TTL_MS = 60_000;
const REVIEW_CACHE_TTL_MS = 30_000;
const sellerCache = new Map<string, { data: SellerInfo; ts: number }>();
const sellerInflight = new Map<string, Promise<SellerInfo | null>>();
const reviewCache = new Map<string, { data: ReviewInfo; ts: number }>();
const reviewInflight = new Map<string, Promise<ReviewInfo | null>>();
const userReviewCache = new Map<string, { hasReview: boolean; ts: number }>();
const userReviewInflight = new Map<string, Promise<boolean>>();

export async function fetchSellerInfoCached(firestore: Firestore, sellerId: string): Promise<SellerInfo | null> {
  return fetchSellerInfo(firestore, sellerId);
}

/**
 * A product's review count and average, for a caller that holds only its id.
 *
 * Read from the product document's own aggregates, which functions recount
 * from both review subcollections on every review written or deleted. This
 * used to read both subcollections in full (every review the product had) to
 * arrive at the same two numbers. A caller that already holds the product
 * should read `rating` / `reviewCount` off it and skip the read altogether.
 */
export async function fetchReviewInfoCached(firestore: Firestore, productId: string): Promise<ReviewInfo | null> {
  return fetchReviewInfo(firestore, productId);
}

async function fetchSellerInfo(firestore: Firestore, sellerId: string): Promise<SellerInfo | null> {
  const cached = sellerCache.get(sellerId);
  if (cached && Date.now() - cached.ts < SELLER_CACHE_TTL_MS) return cached.data;
  const existing = sellerInflight.get(sellerId);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const snap = await getDoc(doc(firestore, 'users', sellerId));
      if (!snap.exists()) return null;
      const data = snap.data() as any;
      const showPfp = data?.settings?.showProfilePicture ?? true;
      const info: SellerInfo = {
        showPfp,
        avatarUrl: showPfp ? (data?.avatarUrl || '') : '',
        username: typeof data?.username === 'string' ? data.username : '',
      };
      sellerCache.set(sellerId, { data: info, ts: Date.now() });
      return info;
    } finally {
      sellerInflight.delete(sellerId);
    }
  })();
  sellerInflight.set(sellerId, promise);
  return promise;
}

async function fetchUserHasReview(firestore: Firestore, productId: string, uid: string): Promise<boolean> {
  const key = `${productId}:${uid}`;
  const cached = userReviewCache.get(key);
  if (cached && Date.now() - cached.ts < REVIEW_CACHE_TTL_MS) return cached.hasReview;
  const existing = userReviewInflight.get(key);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const snap = await getDoc(doc(firestore, 'products', productId, 'reviews', uid));
      const hasReview = snap.exists();
      userReviewCache.set(key, { hasReview, ts: Date.now() });
      return hasReview;
    } finally {
      userReviewInflight.delete(key);
    }
  })();
  userReviewInflight.set(key, promise);
  return promise;
}

async function fetchReviewInfo(firestore: Firestore, productId: string): Promise<ReviewInfo | null> {
  const cached = reviewCache.get(productId);
  if (cached && Date.now() - cached.ts < REVIEW_CACHE_TTL_MS) return cached.data;
  const existing = reviewInflight.get(productId);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const snap = await getDoc(doc(firestore, 'products', productId));
      if (!snap.exists()) return null;
      const { rating, reviewCount } = catalogAggregates(snap.data());
      const info: ReviewInfo = { count: reviewCount, rating };
      reviewCache.set(productId, { data: info, ts: Date.now() });
      return info;
    } finally {
      reviewInflight.delete(productId);
    }
  })();
  reviewInflight.set(productId, promise);
  return promise;
}

type ProductCardProps = {
  id: string;
  name: string;
  seller: string;
  sellerId: string;
  sellerDeleted?: boolean;
  price: number;
  discountedPrice?: number;
  priority?: boolean;
  image: string | ProductImageObject;
  aiHint: string;
  shortDescription?: string;
  category?: string;
  rating?: number;
  reviews?: number;
  tags?: string[];
  layout?: 'grid' | 'list';
  [key: string]: any;
};

// NO RIBBON BADGES. The card used to pin badges down its left edge from the
// product's top-level `tags` list: "secure" ("a high security score, ensuring
// its files are safe and reliable"), "growing", "boosted" and "discounted".
// That list was the seller's to write (firestore.rules left it client-writable
// until it was locked) and nothing on the platform ever wrote it, so every one
// of those badges was a claim a seller could award themselves, the first about
// a security scan that does not exist. Legacy values on existing documents are
// now simply not rendered.
//
// Nothing real was lost. A sale shows as the struck-through price, which comes
// from the product's actual discountedPrice; "discounted" from the list could
// disagree with it either way. A featured listing shows as a "Featured" label
// on its cover (it was a pulsing glow until 2026-10-23), from the `featured`
// flag, which the list's own "featured" entry was always filtered in favour
// of. "growing" and "boosted" had no state behind them at all.

/** The "Featured" label on a cover: the brand tint, one of the brand's marks
 *  (DESIGN.md, The Brand Jobs Rule), in the theme's text colour so it reads on
 *  any cover. */
const FEATURED_BADGE =
  'pointer-events-none absolute left-2 top-2 rounded-full bg-brand-tint px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm';
// Bottom left, so it never sits on the Featured badge.
const BUNDLE_BADGE =
  'pointer-events-none absolute left-2 bottom-2 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm';

function useProductCardState(id: string, sellerId: string, price: number, discountedPrice: number | undefined, name: string, imageUrl: string, pricingModel?: string) {
    const { authUser } = useAuth();
    const { isOwned } = useOwnedProducts();
    const { addToCart } = useCart();
    const { saved, toggleSave } = useWishlist();
    const router = useRouter();

    const [isGuestOwned, setIsGuestOwned] = useState(false);
    useEffect(() => {
        try {
            const stored: string[] = JSON.parse(sessionStorage.getItem('guestOwnedProducts') || '[]');
            setIsGuestOwned(stored.includes(id));
        } catch { /* ignore */ }
    }, [id]);

    const isSeller = !!authUser && authUser.uid === sellerId;
    const isProductOwned = !isSeller && (isOwned(id) || isGuestOwned);
    const isSaved = saved.some(s => s.product.id === id);

    // Products a card can't add straight to the cart, so the button routes to
    // the product page instead:
    //  - tiered / pay-what-you-want: the buyer still has to pick a tier (which
    //    also decides whether the purchase is one-off or recurring) or type an
    //    amount, and a card-added line is one the server refuses to price.
    //  - subscription: recurring purchases are account-only and carry consent
    //    copy the card has no room for (it shows the per-cycle price, but not
    //    the mandate wording). The product page is where the sign-in gate and
    //    that wording live, so the buyer has to land there rather than acquire
    //    a recurring line silently.
    const needsChoiceOnProductPage =
        pricingModel === 'tiered' || pricingModel === 'pwyw' || pricingModel === 'subscription';

    const handleAddToCart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (needsChoiceOnProductPage) {
            router.push(`/product/${id}`);
            return;
        }
        // Free listings add at 0 without consulting the price fields, which on a
        // product switched to Free before the write-side fix still hold the
        // seller's old amount. See src/lib/free-pricing.ts.
        //
        // `from` is where the add-to-cart star launches. It is measured inside
        // the call, which matters here: ActionButtons is redefined on every
        // render, so this button is replaced by the re-render the add causes.
        addToCart(
            { id, name, price: cartPriceAud({ pricingModel, price, discountedPrice }), image: imageUrl },
            { from: e.currentTarget },
        );
    };

    const handleToggleSave = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleSave({ id, name, price: cartPriceAud({ pricingModel, price, discountedPrice }), image: imageUrl } as any);
    };

    return { authUser, isSeller, isProductOwned, isSaved, handleAddToCart, handleToggleSave, needsChoiceOnProductPage };
}

/**
 * A card image with a spinner while it loads.
 *
 * The spinner sits BENEATH the image, and the image is never hidden. Cards are
 * rendered on the server now (the marketplace's first screen, storefronts), and
 * an image that waited for `onLoad` to show itself stayed invisible behind a
 * spinner until the page had hydrated, however long ago it had actually
 * arrived. Beneath it, the spinner shows exactly while nothing has painted over
 * it, with or without JavaScript, and is dropped once the load is seen.
 */
export const ImageWithSpinner = ({
  src, alt, aiHint, useFill = false, priority = false,
}: {
  src: string; alt: string; aiHint?: string; useFill?: boolean; priority?: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={cn("relative w-full h-full", !useFill && "aspect-square")}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
        </div>
      )}
      {useFill ? (
        <Image
          src={src}
          alt={alt}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          priority={priority}
          unoptimized={skipImageOptimizer(src)}
          className="object-cover group-hover:scale-105 motion-reduce:group-hover:scale-100 transition-transform duration-300"
          onLoad={() => setLoaded(true)}
        />
      ) : (
        <Image
          src={src}
          alt={alt}
          width={600}
          height={600}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          priority={priority}
          unoptimized={skipImageOptimizer(src)}
          // `relative` puts the image in the same stacking layer as the
          // spinner, where coming later in the page puts it on top. A static
          // image would paint beneath any positioned sibling.
          className="relative aspect-square object-cover group-hover:scale-105 motion-reduce:group-hover:scale-100 transition-transform duration-300"
          data-ai-hint={aiHint}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

const ProductCardComponent = ({ id, name, seller, sellerId, sellerDeleted, price, discountedPrice, image, aiHint, shortDescription, rating, reviews, reviewCount, avgRating, totalReviews, layout = 'grid', status, featured, pricingModel, pwywMinPrice, pwywMinPriceEnabled, priceTiers, subscriptionPrice, subscriptionFrequency, priority = false, productKind, bundle, bundleSize }: ProductCardProps & { status?: string; featured?: boolean; pricingModel?: string; pwywMinPrice?: number | null; pwywMinPriceEnabled?: boolean; priceTiers?: Array<{ price?: number; purchaseType?: string; subscriptionFrequency?: string }>; subscriptionPrice?: number | null; subscriptionFrequency?: string; productKind?: string; bundle?: unknown; bundleSize?: number }) => {
  const { formatCurrency, toDisplayAmount, toBaseAmount, displayCurrency } = useTheme();
  // A bundle (src/lib/product-bundles.ts) says how many products it holds: off
  // the server's card (bundleSize) or the live document, which agree.
  const bundleCount = typeof bundleSize === 'number' ? bundleSize : bundleSizeOf({ productKind, bundle });
  const { user } = useAuth();
  const { firestore } = useFirebase();
  const imageUrl = typeof image === 'string' ? image : image.src;
  const { authUser, isSeller, isProductOwned, isSaved, handleAddToCart, handleToggleSave, needsChoiceOnProductPage } = useProductCardState(id, sellerId, price, discountedPrice, name, imageUrl, pricingModel);

  // Pay-what-you-want is worded rather than priced on the card (see
  // priceDisplay below); this flag only keeps the struck-through list price off
  // a model that has no list price.
  const isPwyw = pricingModel === 'pwyw';

  // The cart button doesn't always add to the cart (see
  // needsChoiceOnProductPage), so the label says what the click actually does.
  const cardActionLabel = pricingModel === 'subscription'
    ? 'Subscribe'
    : needsChoiceOnProductPage
      ? 'Choose an option'
      : 'Add to cart';

  // Tiered products carry no top-level price (each tier has its own), so the
  // card advertises the cheapest tier as an entry point — "From A$10", plus the
  // cadence when that tier is a recurring one. Without this the card would read
  // A$0.00 for every tiered product.
  //
  // The cheapest tier is resolved by the shared helper rather than a local
  // reduce, so the number this card shows and the number the marketplace
  // FILTERS on come from one place. They did not: the browse filters used
  // `discountedPrice || price` for every model, which is 0 here, so a tiered
  // listing advertising "From A$25" was hidden by a A$10 minimum-price filter.
  const isTiered = pricingModel === 'tiered' && Array.isArray(priceTiers) && priceTiers.length > 0;
  const cheapestTier = isTiered ? (cheapestPriceTier(priceTiers) ?? undefined) : undefined;

  // A whole-product subscription's price is per cycle, so the card shows the
  // cadence alongside it ("A$20 monthly") the way the product page does. Without
  // the suffix the number reads as a one-off purchase price. The price itself
  // lives in `subscriptionPrice`; `price` is the fallback for older documents
  // written before that field split out.
  const isSubscription = pricingModel === 'subscription';

  // One number per pricing model, so both layouts below render the same value,
  // and one resolver across the whole site (see src/lib/listing-price.ts) so the
  // card, the browse filters and the server-rendered catalog pages agree.
  const displayPrice = listingPriceAud({
    pricingModel, price, discountedPrice, priceTiers,
    pwywMinPrice, pwywMinPriceEnabled, subscriptionPrice,
  });
  const cycleSuffix = isSubscription
    ? formatCycleSuffix(subscriptionFrequency)
    : isSubscriptionTier(cheapestTier)
      ? formatCycleSuffix(tierFrequency(cheapestTier))
      : '';
  const showStruckPrice = !isPwyw && !isTiered && !isSubscription && pricingModel !== 'free' && discountedPrice !== undefined && discountedPrice !== null && discountedPrice < price;

  // Whether the figure above is a FLOOR, and so has to be labelled "From".
  //
  // Resolved by the shared helper rather than by `isTiered` alone, which is
  // what this card used to do. Pay-what-you-want advertises a floor just as
  // tiers do, so the same listing read "From A$15" on a server-rendered catalog
  // or profile page and a bare "A$15" here, off the same stored number.
  //
  // The `> 0` guard is not a special case, it is the other half of the catalog's
  // rule: `priceLabel` in src/lib/public-catalog.ts drops to "Name your price"
  // when a floor listing has no floor, so neither surface ever prefixes "From"
  // to a zero. On this card a PWYW listing is worded instead (priceDisplay
  // below), so the label is only ever drawn for tiers now.
  const showFromLabel = listingPriceIsFrom({ pricingModel }) && displayPrice > 0;

  // Free and pay-what-you-want are WORDED on the card (src/lib/listing-price.ts):
  // "Free" instead of A$0.00, and "Buyer's choice" with the seller's floor as
  // "A$5+" when they set one. displayPrice above is unchanged, and it is what
  // sorting and the price filters read, so neither moves.
  const priceDisplay = listingPriceDisplay({ pricingModel, price, discountedPrice, priceTiers, pwywMinPrice, pwywMinPriceEnabled, subscriptionPrice });
  // The floor exactly as the product page's "Minimum" states it: the amount
  // its field opens on, in the visitor's currency (pwywMinimumLabelAud), so the
  // card cannot name a figure one unit under what checkout accepts.
  const pwywMinimumText = priceDisplay.kind === 'pwyw' && priceDisplay.minimumAud !== null
    ? formatCurrency(pwywMinimumLabelAud(priceDisplay.minimumAud, toDisplayAmount, toBaseAmount, displayCurrency))
    : null;

  // The review count and average, from the product's own aggregates. Functions
  // recount avgRating and totalReviews from BOTH review subcollections on every
  // review written or deleted, so they are current; the card used to read both
  // subcollections in full for every product on screen to arrive at the same
  // two numbers.
  //
  // Through catalogAggregates, so a card reads them exactly as the live
  // catalogue does: `avgRating` / `totalReviews` where the server wrote them,
  // else `rating` / `reviewCount`. The order matters for a call site that
  // passes a RAW document, whose `rating` and `reviewCount` are the zeros the
  // create form wrote; a normalised product carries the same figure under both
  // names. `reviews` is the count the call site worked out itself, the last
  // resort.
  const { rating: cardRating, reviewCount: cardReviewCount } = catalogAggregates({
    avgRating, totalReviews, rating, reviewCount: reviewCount ?? reviews,
  });

  const [hasReviewed, setHasReviewed] = useState<boolean>(() => {
    if (!authUser?.uid) return false;
    const cached = userReviewCache.get(`${id}:${authUser.uid}`);
    return cached?.hasReview ?? false;
  });
  useEffect(() => {
    if (!firestore || !id || !authUser?.uid || !isProductOwned) return;
    let cancelled = false;
    fetchUserHasReview(firestore, id, authUser.uid).then(has => {
      if (!cancelled) setHasReviewed(has);
    });
    return () => { cancelled = true; };
  }, [firestore, id, authUser?.uid, isProductOwned]);

  const [sellerAvatarUrl, setSellerAvatarUrl] = useState(() => {
    const cached = sellerId ? sellerCache.get(sellerId) : null;
    return cached?.data.avatarUrl ?? '';
  });
  const [showSellerPfp, setShowSellerPfp] = useState(() => {
    const cached = sellerId ? sellerCache.get(sellerId) : null;
    return cached?.data.showPfp ?? false;
  });
  // Read the current username live from the users doc. Products' denormalized
  // `seller` field is updated by the updateUsername fan-out, but until that
  // sweep lands (and for any product whose seller renamed mid-session) the
  // prop is stale — fall back to it, but prefer the freshly-fetched value.
  const [liveSellerName, setLiveSellerName] = useState(() => {
    const cached = sellerId ? sellerCache.get(sellerId) : null;
    return cached?.data.username ?? '';
  });
  useEffect(() => {
    if (!firestore || !sellerId) return;
    let cancelled = false;
    fetchSellerInfo(firestore, sellerId).then(info => {
      if (cancelled || !info) return;
      setShowSellerPfp(info.showPfp);
      setSellerAvatarUrl(info.avatarUrl);
      if (info.username) setLiveSellerName(info.username);
    });
    return () => { cancelled = true; };
  }, [firestore, sellerId]);
  const displaySeller = liveSellerName || seller;

  const isDeleted = status === 'deleted';
  const isArchived = status === 'archived';
  // A withdrawn listing is described by the badge, never by the label. 'Selling'
  // on a card the seller has taken off sale, or 'Owned' with nothing to say the
  // listing is no longer available, would each be a plainly false statement
  // about the product's state. The review action stays available on an archived
  // card, unlike a deleted one, because this listing is coming back.
  const isWithdrawn = isDeleted || isArchived;
  // Whether the cart button on this card leads anywhere. The checkout callable
  // refuses any product that is not live, so offering Add to cart on one is an
  // affordance whose only possible outcome is a failure at the payment step.
  // Written as an allowlist (with a missing status treated as a legacy live
  // product, the way every other consumer does) rather than as "not archived",
  // so 'reviewing' and 'scheduled' are covered by the same guard. The card is
  // reachable in this state from a buyer's Saved items, which are deliberately
  // NOT auto-removed on archive: the listing is coming back and the buyer
  // should keep it on their list.
  const isPurchasable = !status || status === 'live';
  const statusLabel = isWithdrawn ? null : (isSeller ? 'Selling' : isProductOwned ? 'Owned' : null);
  const productStatusBadge = isDeleted ? { label: 'Deleted', className: '' }
    : isArchived ? { label: 'Archived', className: '' }
    : null;

  const ActionButtons = () => {
    if (isSeller) {
      return (
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="outline" size="icon" className="h-8 w-8 coarse:h-10 coarse:w-10 shrink-0">
                  <Link href={`/dashboard/products/edit/${id}`} onClick={e => e.stopPropagation()} aria-label={`Edit ${name}`}>
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Edit product</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="outline" size="icon" className="h-8 w-8 coarse:h-10 coarse:w-10 shrink-0">
                  <Link href={`/dashboard/analytics?product=${id}`} onClick={e => e.stopPropagation()} aria-label={`View analytics for ${name}`}>
                    <BarChart2 className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>View analytics</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }
    if (isProductOwned) {
      return (
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="outline" size="icon" className="h-8 w-8 coarse:h-10 coarse:w-10 shrink-0">
                  <Link href={`/product/${id}?action=download`} onClick={e => e.stopPropagation()} aria-label={`Download ${name}`}>
                    <Download className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Download</p></TooltipContent>
            </Tooltip>
            {!isDeleted && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="outline" size="icon" className="h-8 w-8 coarse:h-10 coarse:w-10 shrink-0">
                  <Link href={`/product/${id}?action=review`} onClick={e => e.stopPropagation()} aria-label={hasReviewed ? `Edit your review for ${name}` : `Write a review for ${name}`}>
                    <StarIcon className={cn('h-4 w-4', hasReviewed && 'text-[hsl(var(--star))] fill-[hsl(var(--star))]')} aria-hidden />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>{hasReviewed ? 'Edit your review' : 'Write a review'}</p></TooltipContent>
            </Tooltip>
            )}
          </TooltipProvider>
        </div>
      );
    }
    const isGuest = !authUser || authUser.isAnonymous || (!authUser.emailVerified && user === null);
    return (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          {!isGuest && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={cn("h-8 w-8 shrink-0", isSaved && "text-link")}
                  onClick={handleToggleSave}
                  aria-label={isSaved ? `Remove ${name} from saved` : `Save ${name}`}
                  aria-pressed={isSaved}
                >
                  <Bookmark className={cn("h-4 w-4", isSaved && "fill-current")} aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>{isSaved ? 'Remove from saved' : 'Save'}</p></TooltipContent>
            </Tooltip>
          )}
          {isPurchasable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleAddToCart}
                aria-label={cardActionLabel === 'Add to cart' ? `Add ${name} to cart` : `${cardActionLabel} for ${name}`}
              >
                <ShoppingCart className="h-4 w-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>{cardActionLabel}</p></TooltipContent>
          </Tooltip>
          )}
        </TooltipProvider>
      </div>
    );
  };

  // The seller's name under the title: a link to their storefront, or
  // "deleted" for an account that is gone. One copy, so both layouts say it the
  // same way. It shares a row with the rating (sellerRow), so it can shrink
  // and truncate, and the link sits above the card-wide product link
  // (`relative z-10`).
  const sellerLine = displaySeller ? (sellerDeleted ? (
    <div className="flex min-w-0 items-center gap-1.5 w-fit">
      {/* No uid passed: an account that is gone is the plain silhouette. */}
      {showSellerPfp && <UserAvatar className="h-4 w-4 shrink-0" />}
      <span className="text-[11px] italic text-muted-foreground truncate pr-0.5">
        Account closed
      </span>
    </div>
  ) : (
    <Link
      href={storefrontHref(displaySeller)}
      {...STOREFRONT_LINK_PROPS}
      className="relative z-10 flex min-w-0 items-center gap-1.5 group/seller w-fit"
      onClick={e => e.stopPropagation()}
    >
      {/* Their picture, or their initial when they have none. */}
      {showSellerPfp && (
        <UserAvatar className="h-4 w-4 shrink-0" src={sellerAvatarUrl} seed={sellerId} name={displaySeller} />
      )}
      <span className="text-[11px] text-muted-foreground underline underline-offset-2 truncate group-hover/seller:text-foreground transition-colors">
        {displaySeller}
      </span>
    </Link>
  )) : null;

  // The seller on the left and the rating on the right, under the
  // description (owner's layout, 2026-10-21). The rating keeps to the right
  // even when there is no seller line to push it there.
  const sellerRow = (sellerLine || cardReviewCount > 0) ? (
    <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
      {sellerLine}
      {cardReviewCount > 0 && (
        <StarRating compact rating={cardRating} totalReviews={cardReviewCount} className="ml-auto shrink-0" />
      )}
    </div>
  ) : null;

  // The whole card opens the product: the name's link stretches over it
  // (`after:absolute after:inset-0`) now that the View Details button is gone,
  // and the seller link and the action buttons sit above that layer.
  // The VAT note explains itself in a `title` tooltip, which the stretched
  // link above would otherwise swallow; lifted over it like the actions.
  const VAT_ABOVE_LINK = "relative z-10";
  const STRETCHED_LINK = "min-w-0 flex-1 after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring";

  // The wide card: one featured product above a storefront's tabs. Below `sm`
  // it stacks into the grid card's shape, because a cover beside the price row
  // and its buttons leaves too little room on a phone. From `sm` the cover is
  // half the card and from `lg` a third, so it is never smaller than the
  // covers of the grid below it (half of two columns, a third of three).
  if (layout === 'list') {
    return (
        <Card
          className="overflow-hidden group transition-[transform,box-shadow] duration-200 ease-out hover:shadow-lg hover:-translate-y-1 motion-reduce:hover:translate-y-0 bg-card border-border/50 flex flex-col sm:flex-row relative"
        >
          <Link href={`/product/${id}`} className="block relative w-full sm:w-1/2 lg:w-1/3 shrink-0 self-start">
            {/* The square comes from here, as on the grid card, not from the
                image: a product with no cover still gets a full-size tile
                rather than a strip as tall as the placeholder's icon. */}
            <div className="aspect-square relative">
              {imageUrl
                ? <ImageWithSpinner src={imageUrl} alt={name} aiHint={aiHint} priority={priority} />
                : <PlaceholderMedia type="image" />}
              {featured && <span className={FEATURED_BADGE}>Featured</span>}
              {bundleCount > 0 && <span className={BUNDLE_BADGE}>Bundle of {bundleCount} products</span>}
            </div>
          </Link>
          <div className="p-4 flex flex-col flex-1 min-w-0">
            <div className="flex-grow">
              <div className="flex items-start justify-between gap-2">
                <Link href={`/product/${id}`} className={STRETCHED_LINK}>
                  {/* The seller's heading font on their own pages (their
                      storefront, their suggested products); everywhere else
                      the variable is unset and this is the site font. */}
                  <h3 className="truncate font-seller-heading">{name}</h3>
                </Link>
                <div className="shrink-0 flex flex-row items-center gap-1">
                  {productStatusBadge && (
                    isWithdrawn
                      ? <span className="text-xs text-muted-foreground italic select-none">{productStatusBadge.label}</span>
                      : <span className={`text-xs font-medium ${productStatusBadge.className}`}>{productStatusBadge.label}</span>
                  )}
                  {statusLabel && (
                    <span className="text-xs text-muted-foreground italic">{statusLabel}</span>
                  )}
                </div>
              </div>
              {shortDescription && <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">{shortDescription}</p>}
              {sellerRow}
            </div>
            {/* Same order as the grid card: the price on the left, the actions on
                the right, no View Details button. */}
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 mt-4">
                <div>
                  <div className="flex items-baseline gap-2">
                    <p className="font-bold text-2xl mt-2">
                      {priceDisplay.kind === 'free' ? 'Free' : priceDisplay.kind === 'pwyw' ? (
                        <>
                          Buyer&apos;s choice
                          {pwywMinimumText && <span className="text-sm font-medium text-muted-foreground">, {pwywMinimumText}+</span>}
                        </>
                      ) : (
                        <>
                          {showFromLabel && <span className="text-sm font-medium text-muted-foreground mr-1">From</span>}
                          {formatCurrency(displayPrice)}
                          {cycleSuffix && <span className="text-sm font-medium text-muted-foreground ml-1">{cycleSuffix}</span>}
                        </>
                      )}
                    </p>
                    {showStruckPrice && (
                      <span className="text-sm text-muted-foreground line-through">{formatCurrency(price)}</span>
                    )}
                    {/* Nothing to qualify on a free listing. On pay-what-you-want,
                        whatever the buyer names is taxed for an EU or UK buyer
                        (checkout never takes less than A$1), so it stays with or
                        without a floor, as on the product page. */}
                    {priceDisplay.kind === 'pwyw' ? <VatNotice className={VAT_ABOVE_LINK} /> : priceDisplay.kind === 'price' && <VatNotice amount={displayPrice} className={VAT_ABOVE_LINK} />}
                  </div>
                </div>
              <div className="relative z-10 ml-auto">
                <ActionButtons />
              </div>
            </div>
          </div>
        </Card>
    );
  }

  return (
      <Card
        className="overflow-hidden group transition-[transform,box-shadow] duration-200 ease-out hover:shadow-lg hover:-translate-y-1 motion-reduce:hover:translate-y-0 bg-card border-border/50 flex flex-col relative"
      >
        <Link href={`/product/${id}`} className="block relative">
          <div className="aspect-square relative">
              {imageUrl ? (
                  <>
                    <ImageWithSpinner src={imageUrl} alt={name} aiHint={aiHint} priority={priority} />
                  </>
              ) : <PlaceholderMedia type="image" />}
              {featured && <span className={FEATURED_BADGE}>Featured</span>}
              {bundleCount > 0 && <span className={BUNDLE_BADGE}>Bundle of {bundleCount} products</span>}
          </div>
        </Link>
        <div className="p-2 md:p-4 flex flex-col flex-grow bg-secondary/20 border-t border-border/50">
          <div className="flex-grow">
            <div className="flex items-start justify-between gap-2">
              <Link href={`/product/${id}`} className={STRETCHED_LINK}>
                <h3 className="truncate font-seller-heading">{name}</h3>
              </Link>
              <div className="shrink-0 flex flex-row items-center gap-1">
                {productStatusBadge && (
                  isWithdrawn
                    ? <span className="text-xs text-muted-foreground italic select-none">{productStatusBadge.label}</span>
                    : <span className={`text-xs font-medium ${productStatusBadge.className}`}>{productStatusBadge.label}</span>
                )}
                {statusLabel && (
                  <span className="text-xs text-muted-foreground italic">{statusLabel}</span>
                )}
              </div>
            </div>
            {shortDescription && <p className="text-xs md:text-sm text-muted-foreground mt-1 h-8 md:h-10 line-clamp-2 break-words">{shortDescription}</p>}
            {sellerRow}
          </div>
          {/* The price on the left and the actions on the right. The bar of
              View Details and actions that used to close the card is gone
              (owner's layout, 2026-10-21). On a narrow card with a long price
              ("Buyer's choice, $5+") the actions wrap to their own line and
              keep to the right. */}
          <div className="flex flex-wrap items-center justify-between mt-2 md:mt-4 gap-2">
              <div className="flex items-baseline gap-1 md:gap-2">
                  <p className="font-bold text-lg md:text-2xl">
                    {priceDisplay.kind === 'free' ? 'Free' : priceDisplay.kind === 'pwyw' ? (
                      <>
                        Buyer&apos;s choice
                        {pwywMinimumText && <span className="text-xs md:text-sm font-medium text-muted-foreground">, {pwywMinimumText}+</span>}
                      </>
                    ) : (
                      <>
                        {showFromLabel && <span className="text-xs md:text-sm font-medium text-muted-foreground mr-1">From</span>}
                        {formatCurrency(displayPrice)}
                        {cycleSuffix && <span className="text-xs md:text-sm font-medium text-muted-foreground ml-1">{cycleSuffix}</span>}
                      </>
                    )}
                  </p>
                  {showStruckPrice && (
                    <span className="text-xs md:text-sm text-muted-foreground line-through">{formatCurrency(price)}</span>
                  )}
                  {/* See the list layout above for which models carry the qualifier. */}
                  {priceDisplay.kind === 'pwyw' ? <VatNotice className={VAT_ABOVE_LINK} /> : priceDisplay.kind === 'price' && <VatNotice amount={displayPrice} className={VAT_ABOVE_LINK} />}
              </div>
              <div className="relative z-10 ml-auto">
                <ActionButtons />
              </div>
          </div>
        </div>
      </Card>
  );
};

export const ProductCard = React.memo(ProductCardComponent);
